/* eslint-disable react-hooks/exhaustive-deps */
import  { useEffect } from 'react';
import { Form, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCircleXmark, faMagnifyingGlass, faSort, faPenToSquare, faRotate } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import { useState } from 'react';
import { getUserListRequest } from '../../../redux/modules/settings/settingsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/rootReducer';
import useAxios from '../../../hooks/useAxios';
import { API_ENDPOINTS } from '../../../constants/api';
import { toaster } from '../../../utils/toast';
import { toast } from 'react-toastify';
import XDeleteModal from '../../../component/modal/XDeleteModal';
import XConfirm from '../../../component/confirm/XConfirm';
import XButton from '../../../component/button/XButton';
import { useNavigate } from 'react-router-dom';
import { debounce } from '../../../utils/helper';
import useSidebar from '../../../hooks/useSidebar';
import PaginationNav from '../../../component/pagination/pagination';
import XLoader from '../../../component/loader/XLoader';
import { ROLE } from '../../../utils/enums';

const ROLE_DISPLAY_NAMES: Record<number, string> = {
  [ROLE.ORG_STAFF]: 'User',
  [ROLE.SUPER_ADMIN]: 'Super Admin',
  [ROLE.ORG]: 'Organisation',
  [ROLE.SUPER_ADMIN_STAFF]: 'Super Admin Staff',
  [ROLE.READ_ONLY]: 'Read Only',
  [ROLE.ACCOUNT_ADMIN]: 'Account Admin',
  [ROLE.LOCATION_ADMIN]: 'Location Admin',
  [ROLE.LOCATION_STAFF]: 'Location Staff',
};

const formatRoleName = (roleId?: number, roleName?: string): string => {
  if (roleId && ROLE_DISPLAY_NAMES[roleId]) {
    return ROLE_DISPLAY_NAMES[roleId];
  }
  if (roleName) {
    return roleName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  const enumKey = Object.keys(ROLE).find((key) => ROLE[key as keyof typeof ROLE] === roleId);
  if (enumKey) {
    return enumKey
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  return '-';
};

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role_id?: number;
  role_name?: string;
  location_name?: string;
  can_email_send?: boolean;
  is_active?: boolean;
  is_registration_complete?: boolean;
  is_invite_expired?: boolean;
}
const Users = () => {
  const dispatch = useDispatch();
  const {userlist,loading} = useSelector((state: RootState) => state.settings);
  const { selectedLocationId, role_id } = useSelector((state: RootState) => state.user);
  const isReadOnly = +role_id === ROLE.READ_ONLY;
  const { deleteData, postData } = useAxios();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [sendPermissions, setSendPermissions] = useState<Record<string, boolean>>({});
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [resendCooldowns, setResendCooldowns] = useState<Record<string, number>>({});
  const [showResendModal, setShowResendModal] = useState<boolean>(false);
  const [userToResend, setUserToResend] = useState<User | null>(null);
  const navigate = useNavigate()
  const [queryParams, setQueryParams] = useState({
    search: '',
    page: 1,
    limit: 10,
    status: '',
  });
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [totalPages, setTotalPages] = useState<number>(0);
  const { onShowSidebar } = useSidebar();
  useEffect(() => {
    dispatch(getUserListRequest({ ...queryParams, locationId: selectedLocationId }));
  }, [queryParams, selectedLocationId])
  useEffect(() => {
    const totalContacts = userlist?.totalCount;
    const totalPages = Math.ceil(totalContacts / queryParams?.limit);
    setTotalPages(totalPages);
  }, [userlist?.totalCount])

  useEffect(() => {
    if (userlist?.users) {
      const map: Record<string, boolean> = {};
      userlist.users.forEach((u: any) => {
        map[u.id] = !!u.can_email_send;
      });
      setSendPermissions(map);
    }
  }, [userlist?.users]);

  useEffect(() => {
    const hasActiveCooldown = Object.values(resendCooldowns).some((cd) => cd > 0);
    if (!hasActiveCooldown) return;

    const timer = setInterval(() => {
      setResendCooldowns((prev) => {
        const next: Record<string, number> = {};
        Object.keys(prev).forEach((userId) => {
          if (prev[userId] > 1) {
            next[userId] = prev[userId] - 1;
          }
        });
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldowns]);

  const handleResendInvite = async (userId: string) => {
    if (resendingUserId || resendCooldowns[userId]) return;
    setResendingUserId(userId);
    try {
      const response = await postData(`${API_ENDPOINTS.RESEND_INVITE}/${userId}`, {});
      if (response?.success) {
        toast.success(response?.message || 'Invitation link resent successfully.');
        setResendCooldowns((prev) => ({ ...prev, [userId]: 60 }));
      } else {
        toast.error(response?.message || 'Failed to resend invite.');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to resend invite.');
    } finally {
      setResendingUserId(null);
    }
  };

  const handleToggleSendPermission = async (userId: string, newStatus: boolean) => {
    try {
      setUpdatingUserId(userId);
      setSendPermissions((prev) => ({ ...prev, [userId]: newStatus }));
      const response = await postData(API_ENDPOINTS.TOGGLE_USER_SEND_PERMISSION, {
        userId,
        send_permission: newStatus,
        locationId: selectedLocationId || null,
      });
      if (response && response.success) {
        toaster({ success: true, message: response.message || 'Send permission updated successfully' });
      } else {
        setSendPermissions((prev) => ({ ...prev, [userId]: !newStatus }));
        toaster({ success: false, message: response?.message || 'Failed to update send permission' });
      }
    } catch (error) {
      setSendPermissions((prev) => ({ ...prev, [userId]: !newStatus }));
      toaster({ success: false, message: 'Failed to update send permission' });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setQueryParams((prevParams) => ({ ...prevParams, page: pageNumber }));
  };
  const handleSearchChange = (searchValue: string): void => {
    setQueryParams((prevParams) => ({ ...prevParams, search: searchValue, page: 1 }));
  };

  const handleStatusChange = (statusValue: string): void => {
    setQueryParams((prevParams) => ({ ...prevParams, status: statusValue, page: 1 }));
  };

  const debouncedHandleSearchChange = debounce(handleSearchChange, 500);


const handleDeleteUser =async(id:string)=>{
  try {
  const response=await deleteData(`${API_ENDPOINTS.DELETE_USER}/${id}`);
  if (response && response.success === true) {
    toast(response.message);
    dispatch(getUserListRequest({ ...queryParams, locationId: selectedLocationId }));
  }
  } catch (error) {
    toaster({ success: false, message: "error fetching users" });
  }

}
const handleDeleteIconClick = (user:any) => {
  setSelectedUser(user);
  setShowDeleteModal(true);
};
  const addUserButtonHandler = ()=>{
    navigate('addUser')
  }
const handleEditClick=(id:any)=>{
  navigate(`addUser/${id}`)

}
const handleSortClick = (column: string) => {
  if (sortColumn === column) {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  } else {
    setSortColumn(column);
    setSortOrder('asc');
  }
};
const sortedUsers = [...(userlist?.users || [])].sort((a, b) => {
  if (sortColumn) {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
  }
  return 0;
});

  return (
    <div>
      <div className="main-title d-flex align-items-center justify-content-between">
        <h2 className='d-flex align-items-center'>
          <span className="d-lg-none cursor-pointer" onClick={()=>onShowSidebar()}><FontAwesomeIcon icon={faBars} /></span>Users</h2>
        <XButton buttonText='Add New User' className="btn-theme" disabled={isReadOnly} onClick={isReadOnly ? undefined : addUserButtonHandler}/>
      </div>

      <div className="filter-bar d-flex align-items-center justify-content-between w-100">
        <div className='position-relative search-bar'>
          <Form.Control
            type="search"
            placeholder="Search"
            aria-label="Search"
            onChange={(e) => debouncedHandleSearchChange(e.target.value)}
          />
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </div>
        <div className="select-box d-flex align-items-center w-100 justify-content-end">
          <Form.Group className="d-flex align-items-center" controlId="exampleForm.ControlInput1">
            <Form.Label>Filter By</Form.Label>
            <Form.Select
              aria-label="Filter by Status"
              value={queryParams.status}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="invite_pending">Invite Pending</option>
              <option value="invite_expired">Invite Expired</option>
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      <div className="table-listing all-contacts users-list position-relative">
        <Table responsive>
          <thead>
            <tr>
              <th >First Name <FontAwesomeIcon icon={faSort} onClick={() => handleSortClick('first_name')}/></th>
              <th >Last Name <FontAwesomeIcon icon={faSort} onClick={() => handleSortClick('last_name')}/></th>
              <th>Email Address</th>
              <th>Role</th>
              <th>Location</th>
              <th>Can Send</th>
              <th>Status</th>
              <th>Actions </th>
            </tr>
          </thead>
          
          <tbody>
          {!loading && sortedUsers?.length === 0 &&
            <tr>
              <td colSpan={10}>
            <p className='text-center no-records'>No Users found.</p>
            </td>
            </tr>}

            {!loading && sortedUsers && sortedUsers?.map((user:any) => (
               <tr key={user.id}>
               <td>{user.first_name}</td>
               <td>{user.last_name}</td>
               <td>{user.email}</td>
               <td>{formatRoleName(user.role_id, user.role_name)}</td>
               <td>{user.location_name || '-'}</td>
               <td>
                 <Form.Check
                   type="switch"
                   id={`can-email-send-${user.id}`}
                   checked={sendPermissions[user.id] ?? !!user.can_email_send}
                   disabled={updatingUserId === user.id || user.role_id === ROLE.READ_ONLY || user.role_id === ROLE.ACCOUNT_ADMIN|| !user?.is_registration_complete || isReadOnly}
                   onChange={(e) => handleToggleSendPermission(user.id, e.target.checked)}
                 />
               </td>
               <td>
                 {!user?.is_registration_complete ? (
                   user?.is_invite_expired ? (
                     <span className="text-red">Invite Expired</span>
                   ) : (
                     <span className="text-yellow">Invite Pending</span>
                   )
                 ) : user?.is_active ? (
                   <span className="text-green">Active</span>
                 ) : (
                   <span className="text-red">Inactive</span>
                 )}
               </td>
               <td className='actions'>
                 <div className='d-flex align-items-center'>
                    {!user?.is_registration_complete && !isReadOnly && (
                      (() => {
                        const cooldown = resendCooldowns[user.id] || 0;
                        const isSending = resendingUserId === user.id;

                        if (isSending) {
                          return (
                            <span
                              className="d-inline-flex align-items-center me-2 text-primary"
                              style={{ fontSize: '11px', fontWeight: 500, cursor: 'wait' }}
                            >
                              <FontAwesomeIcon icon={faRotate} spin className="me-1" style={{ color: '#3B6AE3' }} />
                              Sending...
                            </span>
                          );
                        }

                        if (cooldown > 0) {
                          return (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-resend-cd-${user.id}`}>
                                  An invitation was recently sent. Please wait {cooldown}s before resending.
                                </Tooltip>
                              }
                            >
                              <span
                                className="d-inline-flex align-items-center me-2 text-muted"
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  background: '#f1f5f9',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  border: '1px solid #e2e8f0',
                                  cursor: 'not-allowed',
                                  userSelect: 'none',
                                }}
                              >
                                <FontAwesomeIcon icon={faRotate} className="me-1" style={{ fontSize: '10px', color: '#64748b' }} />
                                {cooldown}s
                              </span>
                            </OverlayTrigger>
                          );
                        }

                        return (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`tooltip-resend-${user.id}`}>Resend Invite</Tooltip>}
                          >
                            <span
                              className="cursor-pointer me-2"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setUserToResend(user);
                                setShowResendModal(true);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faRotate}
                                style={{ color: '#3B6AE3' }}
                              />
                            </span>
                          </OverlayTrigger>
                        );
                      })()
                    )}
                   <FontAwesomeIcon icon={faCircleXmark} className={isReadOnly ? 'disabled text-muted cursor-not-allowed' : ''} style={isReadOnly ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                     onClick={isReadOnly ? undefined : () => handleDeleteIconClick(user)}
                   />
                   <FontAwesomeIcon icon={faPenToSquare} className={isReadOnly ? 'disabled text-muted cursor-not-allowed' : ''} style={isReadOnly ? { opacity: 0.4, cursor: 'not-allowed' } : undefined} onClick={isReadOnly ? undefined : () => handleEditClick(user?.id)} />
                 </div>
               </td>
             </tr> 
              ))}       
          </tbody>
        </Table>
        {loading && <XLoader />}
      </div>
          <XDeleteModal
            show={showDeleteModal}
            handleClose={() => setShowDeleteModal(false)}
            title="Delete User"
            body={`Are you sure you want to delete the user ${selectedUser?.first_name} ${selectedUser?.last_name}?`}
            onConfirm={() => {
              if (selectedUser) {
                handleDeleteUser(selectedUser.id);
              }    setShowDeleteModal(false);
            }}
          onCancel={() => setShowDeleteModal(false)} 
    />
      <XConfirm
        title="Resend Invitation"
        message={
          userToResend ? (
            <p className="mb-0">
              Are you sure you want to resend the setup invitation to{" "}
              <strong>{userToResend.first_name} {userToResend.last_name}</strong> ({userToResend.email})?
            </p>
          ) : (
            "Are you sure you want to resend the setup invitation?"
          )
        }
        confirmLabel="Resend Invite"
        cancelLabel="Cancel"
        show={showResendModal}
        onCancel={() => {
          setShowResendModal(false);
          setUserToResend(null);
        }}
        onConfirm={() => {
          if (userToResend?.id) {
            handleResendInvite(userToResend.id);
          }
          setShowResendModal(false);
          setUserToResend(null);
        }}
        isLoading={Boolean(userToResend && resendingUserId === userToResend.id)}
      />
      {!loading && sortedUsers?.length !== 0 && (
        <PaginationNav
          totalPages={totalPages}
          currentPage={queryParams.page}
          handlePageChange={handlePageChange}
        />
      )}
    </div>
  )
}

export default Users
